import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Dialog,
  Field,
  Input,
  PageHead,
  Panel,
  Pill,
  PillGroup,
  ProgressBar,
  Skeleton,
  StatusPill,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { Checkmark16Regular } from "@fluentui/react-icons";
import { useGet, useWrite } from "../api/hooks";
import type { Schema, Subscription as Sub } from "../api/types";
import { PaymentForm } from "../components/PaymentForm";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { daysUntil, formatDate, formatMoney } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { openExternal } from "../teams";

type Invoice = Schema<"InvoiceLink">;
type Plan = Schema<"PlanOffer">;
type Intent = Schema<"SetupIntent">;
type Tax = Schema<"TaxEstimate">;

/** the billing country as the customer reads it (ISO code in, name in the interface language out) */
const countryName = (code: string, locale: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};
type Interval = "monthly" | "annual";

/** Plan, usage, card, billing details and invoices; the card form is Stripe's Elements with its Address Element (D-160), Enterprise is sales-assisted (BR-018, BR-251, BR-261, BR-262). */
export function Subscription() {
  const { t, locale } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const tz = s.me.tenant.timeZone;
  const q = useGet<Sub>("/api/v1/subscription");
  const invoices = useGet<Invoice[]>("/api/v1/subscription/invoices");
  const [interval, setInterval_] = useState<Interval>("monthly");
  const [buy, setBuy] = useState<Plan | null>(null);
  const [change, setChange] = useState<Plan | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  // inside Teams the card is entered in the browser (D-165): the tab waits for the payment and updates itself
  const outside = s.teams.externalPayment === true;
  const qc = useQueryClient();
  const linkRequest = useWrite<Schema<"PaymentLink">, Schema<"PaymentLinkRequest">>(
    "POST",
    "/api/v1/subscription/payment-link",
  );
  const [waiting, setWaiting] = useState<{ snapshot: string; url: string } | null>(null);
  const snapshotOf = (d: Sub | undefined) =>
    d ? [d.status, d.planKey, d.billingInterval, d.paymentMethodLabel ?? ""].join("|") : "";
  const startOutside = (body: Schema<"PaymentLinkRequest">) =>
    linkRequest.mutate(
      { body },
      {
        onSuccess: (r) => {
          setWaiting({ snapshot: snapshotOf(q.data), url: r.url });
          void openExternal(r.url);
        },
        onError: fail,
      },
    );
  useEffect(() => {
    if (!waiting) return;
    const refresh = () => void q.refetch();
    const timer = setInterval(refresh, 3000);
    const visible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", visible);
    const giveUp = setTimeout(() => setWaiting(null), 11 * 60_000); // the link lives ten minutes
    return () => {
      clearInterval(timer);
      clearTimeout(giveUp);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [waiting?.url]);
  useEffect(() => {
    if (waiting && q.data && snapshotOf(q.data) !== waiting.snapshot) {
      setWaiting(null);
      toast.success(t("ui:subscription.changed"));
      for (const p of ["/api/v1/me", "/api/v1/dashboard", "/api/v1/teams", "/api/v1/subscription/invoices"])
        void qc.invalidateQueries({ predicate: (k) => String(k.queryKey[0]).startsWith(p) });
    }
  }, [q.data, waiting?.url]);
  const [sales, setSales] = useState(false);
  const [details, setDetails] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const invalidate = ["/api/v1/subscription", "/api/v1/me", "/api/v1/dashboard", "/api/v1/teams"];
  const subscribe = useWrite<Sub, Schema<"SubscribeRequest">>(
    "POST",
    "/api/v1/subscription/subscribe",
    invalidate,
  );
  const cancel = useWrite<Sub>("POST", "/api/v1/subscription/cancel", invalidate);
  const manage = s.gate("SUBSCRIPTION_MANAGE");
  // the offers open on the period the customer pays (once), so the other one is the offer to switch to
  const periodSet = useRef(false);
  useEffect(() => {
    const d = q.data;
    if (d && !periodSet.current && (d.status === "active" || d.status === "past_due")) {
      periodSet.current = true;
      setInterval_((d.billingInterval ?? "monthly") as Interval);
    }
  }, [q.data]);
  if (q.isLoading || !q.data) return <Skeleton rows={6} />;
  const sub = q.data;
  const trialDays = sub.status === "trial" ? daysUntil(sub.trialEndsAt, new Date()) : null;
  const paying = sub.status === "active" || sub.status === "past_due";
  // billed by Microsoft (Marketplace): plan, card, invoices and cancellation live in the Microsoft 365 admin center (D-166)
  const ms = sub.billingProvider === "microsoft";
  const choose = (p: Plan) => {
    // a plan change is never one click: the customer confirms what will happen and what it costs
    if (paying && sub.paymentMethodLabel) setChange(p);
    else if (outside)
      startOutside({
        purpose: "subscribe",
        planKey: p.planKey as "starter" | "business" | "scale",
        billingInterval: interval,
      });
    else setBuy(p);
  };

  return (
    <div className="gp-page">
      <PageHead title={t("ui:subscription.title")} subtitle={t("ui:subscription.lead")} />
      {sub.status === "trial" ? (
        <Banner
          tone="warn"
          action={
            <Button
              variant="primary"
              disabledReason={manage}
              onClick={() => document.getElementById("plans")?.scrollIntoView()}
            >
              {t("ui:subscription.upgradeNow")}
            </Button>
          }
        >
          {t("ui:subscription.trialBanner", { n: trialDays ?? 0 })}
        </Banner>
      ) : null}
      {sub.status === "past_due" ? (
        <Banner tone="danger">
          {t("ui:subscription.pastDue", { date: formatDate(sub.graceEndsAt, locale, tz) })}
        </Banner>
      ) : null}
      {sub.cancelAtPeriodEnd ? (
        <Banner tone="warn">
          {t("ui:subscription.cancelsOn", { date: formatDate(sub.currentPeriodEnd, locale, tz) })}
        </Banner>
      ) : null}

      {ms ? (
        <Banner
          action={
            <a
              className="gp-btn"
              href="https://admin.microsoft.com/Adminportal/Home#/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("ui:subscription.msAdmin")}
            </a>
          }
        >
          {t("ui:subscription.msBilled")}
        </Banner>
      ) : null}
      {waiting ? (
        <Banner
          action={
            <>
              <Button onClick={() => void openExternal(waiting.url)}>
                {t("ui:subscription.waitingOpenAgain")}
              </Button>
              <Button variant="ghost" onClick={() => setWaiting(null)}>
                {t("ui:subscription.waitingStop")}
              </Button>
            </>
          }
        >
          <span className="gp-row">
            <span className="gp-spinner" aria-hidden />
            {t("ui:subscription.waiting")}
          </span>
        </Banner>
      ) : null}

      <Card title={t("ui:subscription.usage")}>
        <div className="gp-grid-3">
          <div>
            <div className="t-title-sm">
              {sub.effectiveTeamLimit === -1
                ? `${sub.teamsUsed ?? 0}`
                : `${sub.teamsUsed ?? 0} / ${sub.effectiveTeamLimit ?? 0}`}
            </div>
            <span className="t-body-sm gp-muted">{t("ui:subscription.teams")}</span>
            {sub.effectiveTeamLimit !== -1 ? (
              <ProgressBar
                value={sub.teamsUsed ?? 0}
                max={sub.effectiveTeamLimit ?? 0}
                label={t("ui:subscription.teams")}
              />
            ) : null}
          </div>
          <div>
            <div className="t-title-sm">{sub.externalUsersTracked ?? 0}</div>
            <span className="t-body-sm gp-muted">{t("ui:subscription.users")}</span>
          </div>
          <div>
            <div className="t-title-sm">{sub.campaignsRun ?? 0}</div>
            <span className="t-body-sm gp-muted">{t("ui:subscription.campaigns")}</span>
          </div>
        </div>
        <div className="gp-row">
          <StatusPill
            domain="subscription"
            value={sub.status ?? "trial"}
            label={t(`ui:subscription.status.${sub.status}`)}
          />
          {paying && sub.effectivePriceCents !== null && sub.effectivePriceCents !== undefined ? (
            <span className="t-body">
              {t("ui:subscription.price", { price: formatMoney(sub.effectivePriceCents, locale) })}
            </span>
          ) : null}
        </div>
      </Card>

      {ms ? null : (
        <>
          <section id="plans" aria-labelledby="plans-h" className="gp-stack">
            <div className="gp-section-head">
              <div className="gp-stack" style={{ gap: "var(--space-2)" }}>
                <h2 id="plans-h" className="t-title-sm" style={{ margin: 0 }}>
                  {t("ui:subscription.plans")}
                </h2>
                <p className="t-body gp-muted" style={{ margin: 0 }}>
                  {sub.status === "trial"
                    ? t("ui:subscription.plansLeadTrial")
                    : t("ui:subscription.plansLead")}
                </p>
              </div>
              <PillGroup
                legend={t("ui:subscription.interval")}
                name="interval"
                value={interval}
                onChange={(v) => setInterval_(v as Interval)}
                options={[
                  { value: "monthly", label: t("ui:subscription.monthly") },
                  { value: "annual", label: t("ui:subscription.annual") },
                ]}
              />
            </div>
            <div className="gp-plans">
              {(sub.plans ?? []).map((p) => {
                // the plan the customer pays for, and whether the period shown is the one they pay: the same plan in the other period is an offer
                const mine = Boolean(p.current);
                const sameInterval = (sub.billingInterval ?? "monthly") === interval;
                const current = mine && sameInterval;
                return (
                  <PlanCard
                    key={p.planKey}
                    plan={p}
                    interval={interval}
                    used={sub.teamsUsed ?? 0}
                    recommended={p.popular === true && !mine}
                    badge={
                      mine
                        ? t(`ui:subscription.currentIn.${sub.billingInterval ?? "monthly"}`)
                        : p.popular
                          ? t("ui:subscription.popular")
                          : null
                    }
                    featured={mine || p.popular === true}
                    action={
                      p.planKey === "enterprise" ? (
                        <Button disabledReason={manage} onClick={() => setSales(true)}>
                          {t("ui:subscription.contactSales")}
                        </Button>
                      ) : (
                        <Button
                          variant={current ? "default" : "primary"}
                          disabledReason={
                            manage ??
                            (current && !sub.cancelAtPeriodEnd ? t("ui:subscription.alreadyOn") : undefined)
                          }
                          onClick={() => choose(p)}
                        >
                          {current
                            ? t("ui:subscription.keep")
                            : mine && paying
                              ? t(`ui:subscription.switchInterval.${interval}`)
                              : paying
                                ? t("ui:subscription.switch", { plan: p.displayName ?? "" })
                                : sub.status === "trial"
                                  ? t("ui:subscription.keepAfter", { plan: p.displayName ?? "" })
                                  : t("ui:subscription.subscribe")}
                        </Button>
                      )
                    }
                  />
                );
              })}
            </div>
            <ul className="gp-plans-assure t-body-sm gp-muted">
              <li>{t("ui:subscription.everyPlan")}</li>
              <li>{t("ui:subscription.keepData")}</li>
              <li>{t("ui:subscription.cancelAny")}</li>
            </ul>
          </section>

          <Card
            title={t("ui:subscription.billing")}
            actions={
              <Button variant="ghost" disabledReason={manage} onClick={() => setDetails(true)}>
                {t("ui:subscription.edit")}
              </Button>
            }
          >
            <dl className="gp-kv t-body">
              <dt>{t("ui:subscription.contact")}</dt>
              <dd>{sub.billingContactEmail ?? s.me.person.email ?? "—"}</dd>
              <dt>{t("ui:subscription.payment")}</dt>
              <dd>
                {sub.paymentMethodLabel ?? "—"}{" "}
                {paying ? (
                  <Button
                    variant="ghost"
                    disabledReason={manage}
                    onClick={() => (outside ? startOutside({ purpose: "card" }) : setCardOpen(true))}
                  >
                    {t("ui:subscription.changeCard")}
                  </Button>
                ) : null}
              </dd>
            </dl>
            {paying && !sub.cancelAtPeriodEnd ? (
              <div className="gp-actions">
                <Button variant="danger" disabledReason={manage} onClick={() => setConfirmCancel(true)}>
                  {t("ui:subscription.cancel")}
                </Button>
              </div>
            ) : null}
          </Card>

          <section className="gp-stack" aria-labelledby="inv-h">
            <h2 id="inv-h" className="t-title-sm" style={{ margin: 0 }}>
              {t("ui:subscription.invoices")}
            </h2>
            <Panel label={t("ui:subscription.invoices")}>
              <DataTable
                caption={t("ui:subscription.invoices")}
                rows={invoices.data}
                loading={invoices.isLoading}
                error={invoices.isError}
                onRetry={() => void invoices.refetch()}
                errorTitle={t("ui:table.error")}
                retryLabel={t("common:retry")}
                rowKey={(i) => i.number ?? ""}
                emptyTitle={t("ui:subscription.noInvoices")}
                columns={invoiceColumns(t, locale, tz)}
              />
            </Panel>
          </section>
        </>
      )}

      <PurchaseDialog
        plan={buy}
        interval={interval}
        email={sub.billingContactEmail ?? s.me.person.email ?? ""}
        onClose={() => setBuy(null)}
        // the promise settles when the subscription is saved and the page data refreshed: the payment form stays busy until then
        subscribe={async (pm) => {
          if (!buy) return;
          try {
            await subscribe.mutateAsync({
              body: {
                planKey: buy.planKey as "starter" | "business" | "scale",
                billingInterval: interval,
                paymentMethodId: pm,
              },
            });
            setBuy(null);
            toast.success(t("ui:subscription.changed"));
          } catch (e) {
            fail(e);
          }
        }}
      />
      <ChangePlanDialog
        plan={change}
        interval={interval}
        sub={sub}
        onClose={() => setChange(null)}
        confirm={async () => {
          if (!change) return;
          try {
            await subscribe.mutateAsync({
              body: {
                planKey: change.planKey as "starter" | "business" | "scale",
                billingInterval: interval,
              },
            });
            setChange(null);
            toast.success(t("ui:subscription.changed"));
          } catch (e) {
            fail(e);
          }
        }}
      />
      <CardDialog open={cardOpen} onClose={() => setCardOpen(false)} />
      <DetailsDialog
        open={details}
        onClose={() => setDetails(false)}
        email={sub.billingContactEmail ?? s.me.person.email ?? ""}
      />
      <SalesDialog open={sales} onClose={() => setSales(false)} />
      <Dialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title={t("ui:subscription.cancelTitle")}
        description={t("ui:subscription.cancelBody", { date: formatDate(sub.currentPeriodEnd, locale, tz) })}
        footer={
          <>
            <Button onClick={() => setConfirmCancel(false)}>{t("common:cancel")}</Button>
            <Button
              variant="danger"
              onClick={() =>
                cancel.mutate(undefined, { onSuccess: () => setConfirmCancel(false), onError: fail })
              }
            >
              {t("ui:subscription.cancelConfirm")}
            </Button>
          </>
        }
      />
    </div>
  );
}

function invoiceColumns(
  t: ReturnType<typeof useI18n>["t"],
  locale: Parameters<typeof formatDate>[1],
  tz: string | undefined,
): Column<Invoice>[] {
  return [
    {
      key: "n",
      header: t("ui:subscription.inv.number"),
      render: (i) => <span className="t-mono">{i.number}</span>,
    },
    { key: "d", header: t("ui:exports.col.date"), render: (i) => formatDate(i.issuedAt, locale, tz) },
    {
      key: "a",
      header: t("ui:subscription.inv.amount"),
      numeric: true,
      render: (i) => formatMoney(i.amountCents ?? 0, locale, i.currency ?? "EUR"),
    },
    {
      key: "s",
      header: t("ui:users.col.status"),
      render: (i) => (
        <StatusPill
          domain="invoice"
          value={i.status ?? "pending"}
          label={t(`ui:invoiceStatus.${i.status}`)}
        />
      ),
    },
    {
      key: "p",
      header: t("ui:table.actions"),
      render: (i) =>
        i.hostedPdfUrl ? (
          <a href={i.hostedPdfUrl} target="_blank" rel="noopener noreferrer" className="t-body-strong">
            {t("ui:exports.download")}
          </a>
        ) : null,
    },
  ];
}

const VAT_FORMAT = /^[A-Z]{2}[0-9A-Z]{2,12}$/;
const normaliseVat = (v: string): string => v.replace(/[\s.-]/g, "").toUpperCase();
const EMAIL = /^\S+@\S+\.\S+$/;

/** The billing email and the VAT number: checked here, sent to Stripe by the API (D-160). */
function useBillingContact(initialEmail: string) {
  const { t } = useI18n();
  const [email, setEmail] = useState(initialEmail);
  const [vat, setVat] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const check = (): boolean => {
    const e: Record<string, string> = {};
    if (!EMAIL.test(email.trim())) e["email"] = t("ui:subscription.emailInvalid");
    if (vat.trim() && !VAT_FORMAT.test(normaliseVat(vat))) e["vat"] = t("ui:subscription.vatInvalid");
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const body = () => ({ billingContactEmail: email.trim(), vatNumber: normaliseVat(vat) });
  /** the VAT number as the VAT estimate takes it: only when it is well formed */
  const vatValue = VAT_FORMAT.test(normaliseVat(vat)) ? normaliseVat(vat) : "";
  const fields = (
    <>
      <Field
        label={t("ui:subscription.contact")}
        help={t("ui:subscription.contactHelp")}
        error={errors["email"] ?? errors["billingContactEmail"]}
      >
        <Input type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field
        label={t("ui:subscription.vat")}
        help={t("ui:subscription.vatHelp")}
        error={errors["vat"] ?? errors["vatNumber"]}
      >
        <Input value={vat} autoCapitalize="characters" onChange={(e) => setVat(e.target.value)} />
      </Field>
    </>
  );
  return {
    fields,
    check,
    body,
    vatValue,
    setErrors,
    reset: (e: string) => {
      setEmail(e);
      setVat("");
      setErrors({});
    },
  };
}

/**
 * The card step of a purchase: a SetupIntent from the API, confirmed in Stripe's own elements (BR-262). The business name and the
 * address are Stripe's Address Element, the email and the VAT number are ours and go to Stripe (D-160).
 */
/** The order, the billing contact and the card form of a purchase (in the dialog, or on the browser payment page). Mount it to start. */
export function PurchaseBody({
  plan,
  interval,
  email,
  subscribe,
}: {
  plan: Plan;
  interval: Interval;
  email: string;
  subscribe: (pm: string) => void | Promise<unknown>;
}) {
  const { t, locale } = useI18n();
  const intent = useWrite<Intent>("POST", "/api/v1/subscription/setup-intent");
  const saveDetails = useWrite<unknown, Schema<"BillingDetails">>(
    "PUT",
    "/api/v1/subscription/billing-details",
    ["/api/v1/subscription"],
  );
  const [data, setData] = useState<Intent | null>(null);
  const fail = useHandleApiError();
  const contact = useBillingContact(email);
  // VAT is shown before paying: Stripe Tax answers for the billing country typed in the Address Element (D-161); the values are not kept
  const estimate = useWrite<Tax, Schema<"TaxEstimateRequest">>("POST", "/api/v1/subscription/tax-estimate");
  const [addr, setAddr] = useState({ country: "", postalCode: "" });
  const [tax, setTax] = useState<Tax | null>(null);
  const seq = useRef(0);
  const vatValue = contact.vatValue;
  useEffect(() => {
    if (addr.country.length !== 2) {
      setTax(null);
      return;
    }
    const mine = ++seq.current;
    const timer = setTimeout(
      () =>
        estimate.mutate(
          {
            body: {
              planKey: plan.planKey as "starter" | "business" | "scale",
              billingInterval: interval,
              country: addr.country,
              ...(addr.postalCode ? { postalCode: addr.postalCode } : {}),
              ...(vatValue ? { vatNumber: vatValue } : {}),
            },
          },
          {
            onSuccess: (r) => mine === seq.current && setTax(r),
            onError: () => mine === seq.current && setTax(null),
          },
        ),
      400,
    );
    return () => clearTimeout(timer);
  }, [plan?.planKey, interval, addr.country, addr.postalCode, vatValue]);
  useEffect(() => {
    intent.mutate(undefined, { onSuccess: setData, onError: fail });
    // one SetupIntent per opening
  }, []);
  // what the card will be charged: the monthly price, or the yearly one (the tier's annual price, else ten months)
  const cents =
    interval === "annual"
      ? (plan.annualPriceCents ?? (plan.monthlyPriceCents == null ? null : plan.monthlyPriceCents * 10))
      : (plan.monthlyPriceCents ?? null);
  const price = cents === null ? "" : formatMoney(cents, locale);
  // the card is charged the price plus the VAT: the amount on the button is the total once Stripe Tax has answered
  const payLabel =
    tax?.totalCents != null
      ? t("ui:subscription.payAmount", { price: formatMoney(tax.totalCents, locale) })
      : t("ui:subscription.pay");
  const beforeSubmit = async (): Promise<boolean> => {
    if (!contact.check()) {
      document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
      return false;
    }
    try {
      await saveDetails.mutateAsync({ body: contact.body() });
      return true;
    } catch (err) {
      contact.setErrors(fieldErrorsOf(err));
      fail(err);
      return false;
    }
  };
  return (
    <>
      <section className="gp-order" aria-label={t("ui:subscription.orderTitle")}>
        <div>
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {t("ui:subscription.orderTitle")}
          </p>
          <p className="t-body-strong" style={{ margin: 0 }}>
            {plan.displayName}
          </p>
        </div>
        <div className="gp-order-price">
          <span className="t-title-md">{price}</span>
          <span className="t-body-sm gp-muted">
            {t(interval === "annual" ? "ui:subscription.perYear" : "ui:subscription.perMonth")} ·{" "}
            {t("ui:subscription.excl")}
          </span>
        </div>
        {tax && tax.taxCents !== null && tax.totalCents !== null ? (
          <>
            <div className="gp-order-line t-body">
              <span>
                {tax.reverseCharge
                  ? t("ui:subscription.vatLineReverse")
                  : tax.ratePercent
                    ? t("ui:subscription.vatLine", { rate: tax.ratePercent })
                    : t("ui:subscription.vatLineZero")}
              </span>
              <span>{formatMoney(tax.taxCents, locale)}</span>
            </div>
            <div className="gp-order-line gp-order-total t-body-strong">
              <span>{t("ui:subscription.totalLine")}</span>
              <span>
                {formatMoney(tax.totalCents, locale)}{" "}
                {t(interval === "annual" ? "ui:subscription.perYear" : "ui:subscription.perMonth")}
              </span>
            </div>
            <p className="t-body-sm gp-muted gp-order-note">
              {t("ui:subscription.vatEstimate", { country: countryName(addr.country, locale) })}
            </p>
          </>
        ) : (
          <p className="t-body-sm gp-muted gp-order-note">
            {addr.country ? t("ui:subscription.vatUnknown") : t("ui:subscription.vatPending")}
          </p>
        )}
        <p className="t-body-sm gp-muted gp-order-note">{t(`ui:subscription.orderNote.${interval}`)}</p>
      </section>
      <div className="gp-billing-fields">{contact.fields}</div>
      {data?.clientSecret && data.publishableKey ? (
        <PaymentForm
          beforeSubmit={beforeSubmit}
          clientSecret={data.clientSecret}
          publishableKey={data.publishableKey}
          submitLabel={payLabel}
          onAddress={setAddr}
          onPaymentMethod={subscribe}
        />
      ) : (
        <p className="t-body gp-muted" role="status">
          {t("common:loading")}
        </p>
      )}
    </>
  );
}

function PurchaseDialog({
  plan,
  interval,
  email,
  onClose,
  subscribe,
}: {
  plan: Plan | null;
  interval: Interval;
  email: string;
  onClose: () => void;
  subscribe: (pm: string) => void | Promise<unknown>;
}) {
  const { t } = useI18n();
  return (
    <Dialog
      open={plan !== null}
      onClose={onClose}
      size="lg"
      title={t("ui:subscription.purchaseTitle", { plan: plan?.displayName ?? "" })}
      description={t(`ui:subscription.purchaseLead.${interval}`)}
      footer={<Button onClick={onClose}>{t("common:cancel")}</Button>}
    >
      {plan ? (
        <PurchaseBody
          key={plan.planKey}
          plan={plan}
          interval={interval}
          email={email}
          subscribe={subscribe}
        />
      ) : null}
    </Dialog>
  );
}

/** The card form of a card change (in the dialog, or on the browser payment page). Mount it to start. */
export function CardBody({ onPaymentMethod }: { onPaymentMethod: (pm: string) => void | Promise<unknown> }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const intent = useWrite<Intent>("POST", "/api/v1/subscription/setup-intent");
  const [data, setData] = useState<Intent | null>(null);
  useEffect(() => {
    intent.mutate(undefined, { onSuccess: setData, onError: fail });
  }, []);
  return data?.clientSecret && data.publishableKey ? (
    <PaymentForm
      clientSecret={data.clientSecret}
      publishableKey={data.publishableKey}
      submitLabel={t("common:save")}
      onPaymentMethod={onPaymentMethod}
    />
  ) : (
    <p className="t-body gp-muted" role="status">
      {t("common:loading")}
    </p>
  );
}

function CardDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const toast = useToast();
  const update = useWrite<Sub, { paymentMethodId: string }>("PUT", "/api/v1/subscription/payment-method", [
    "/api/v1/subscription",
  ]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t("ui:subscription.changeCard")}
      footer={<Button onClick={onClose}>{t("common:cancel")}</Button>}
    >
      {open ? (
        <CardBody
          onPaymentMethod={async (pm) => {
            try {
              await update.mutateAsync({ body: { paymentMethodId: pm } });
              toast.success(t("ui:settings.saved"));
              onClose();
            } catch (e) {
              fail(e);
            }
          }}
        />
      ) : null}
    </Dialog>
  );
}

function SalesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const toast = useToast();
  const [message, setMessage] = useState("");
  const send = useWrite<unknown, { message: string }>("POST", "/api/v1/contact-sales");
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("ui:subscription.salesTitle")}
      description={t("ui:subscription.salesLead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="primary"
            onClick={() =>
              send.mutate(
                { body: { message } },
                {
                  onSuccess: () => {
                    toast.success(t("ui:subscription.salesSent"));
                    setMessage("");
                    onClose();
                  },
                  onError: fail,
                },
              )
            }
          >
            {t("ui:subscription.salesSend")}
          </Button>
        </>
      }
    >
      <Field label={t("ui:subscription.salesMessage")}>
        <textarea
          className="gp-textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={2000}
        />
      </Field>
    </Dialog>
  );
}

function DetailsDialog({ open, onClose, email }: { open: boolean; onClose: () => void; email: string }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const toast = useToast();
  const save = useWrite<unknown, Schema<"BillingDetails">>("PUT", "/api/v1/subscription/billing-details", [
    "/api/v1/subscription",
  ]);
  const contact = useBillingContact(email);
  useEffect(() => {
    if (open) contact.reset(email);
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("ui:subscription.billing")}
      description={t("ui:subscription.detailsLead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="primary"
            onClick={() => {
              if (!contact.check()) return;
              save.mutate(
                { body: contact.body() },
                {
                  onSuccess: () => {
                    toast.success(t("ui:settings.saved"));
                    onClose();
                  },
                  onError: (err) => {
                    contact.setErrors(fieldErrorsOf(err));
                    fail(err);
                  },
                },
              );
            }}
          >
            {t("common:save")}
          </Button>
        </>
      }
    >
      <div className="gp-billing-fields">{contact.fields}</div>
    </Dialog>
  );
}

/** One offer: the price of the chosen period (and what it comes to per month and per Team), what it holds, whether it fits the Teams in use. */
function PlanCard({
  plan: p,
  interval,
  used,
  badge,
  featured,
  recommended,
  action,
}: {
  plan: Plan;
  interval: Interval;
  used: number;
  badge: string | null;
  featured: boolean;
  recommended: boolean;
  action: React.ReactNode;
}) {
  const { t, locale } = useI18n();
  const enterprise = p.planKey === "enterprise";
  const monthly = p.monthlyPriceCents ?? null;
  const price = interval === "annual" ? (p.annualPriceCents ?? (monthly ? monthly * 10 : null)) : monthly;
  const limit = p.teamsLimit ?? 0;
  const unlimited = limit === -1;
  const hasPrice = price !== null && price !== undefined && !enterprise;
  const saving = hasPrice && interval === "annual" && monthly ? monthly * 12 - (price ?? 0) : 0;
  const fits = unlimited || used <= limit;
  return (
    <section
      className={`gp-plan${featured ? " gp-plan--featured" : ""}${recommended ? " gp-plan--recommended" : ""}`}
      aria-labelledby={`plan-${p.planKey}`}
    >
      <div className="gp-plan-top">
        <h3 id={`plan-${p.planKey}`} className="t-title-sm" style={{ margin: 0 }}>
          {p.displayName}
        </h3>
        {badge ? <Pill variant="brand">{badge}</Pill> : null}
      </div>
      {enterprise ? (
        <>
          <div className="gp-plan-price t-title-md">{t("ui:subscription.custom")}</div>
          <p className="t-body-sm gp-muted gp-plan-sub">{t("ui:subscription.enterpriseLead")}</p>
        </>
      ) : (
        <>
          <div className="gp-plan-price">
            <span className="t-title-lg">{hasPrice ? formatMoney(price ?? 0, locale) : "—"}</span>
            <span className="t-body-sm gp-muted">
              {interval === "annual" ? t("ui:subscription.perYear") : t("ui:subscription.perMonth")} ·{" "}
              {t("ui:subscription.excl")}
            </span>
          </div>
          <p className="t-body-sm gp-muted gp-plan-sub">
            {hasPrice && interval === "annual"
              ? t("ui:subscription.monthEquivalent", {
                  price: formatMoney(Math.round((price ?? 0) / 12), locale),
                })
              : hasPrice && limit > 0
                ? t("ui:subscription.perTeam", {
                    price: formatMoney(Math.round((price ?? 0) / limit), locale),
                  })
                : ""}
          </p>
          {saving > 0 ? (
            <p className="gp-plan-saving t-body-sm">
              {t("ui:subscription.saving", { price: formatMoney(saving, locale) })}
            </p>
          ) : null}
        </>
      )}
      <ul className="gp-plan-list t-body">
        <li>
          <Checkmark16Regular aria-hidden />
          {unlimited ? t("ui:subscription.unlimitedTeams") : t("ui:subscription.teamsUpTo", { n: limit })}
        </li>
        {enterprise ? (
          <li>
            <Checkmark16Regular aria-hidden />
            {t("ui:subscription.enterpriseTerms")}
          </li>
        ) : null}
      </ul>
      {used > 0 && !enterprise ? (
        <p className={`gp-plan-fit t-body-sm${fits ? "" : " gp-plan-fit--warn"}`}>
          {fits ? t("ui:subscription.fits", { n: used }) : t("ui:subscription.tooSmall", { n: used })}
        </p>
      ) : null}
      <div className="gp-plan-cta">{action}</div>
    </section>
  );
}

/** The price of a plan for a billing period, in cents (a yearly price left empty is billed 10 months). */
function periodPrice(p: Plan, interval: Interval): number | null {
  const monthly = p.monthlyPriceCents ?? null;
  return interval === "annual" ? (p.annualPriceCents ?? (monthly ? monthly * 10 : null)) : monthly;
}
const perMonth = (cents: number, interval: Interval) => (interval === "annual" ? cents / 12 : cents);

/**
 * Confirmation of a plan change on a paying subscription (BR-251): what changes, what it costs, and when it applies — immediately with
 * a pro-rata charge for a higher plan, at the next renewal for a lower one.
 */
function ChangePlanDialog({
  plan,
  interval,
  sub,
  onClose,
  confirm,
}: {
  plan: Plan | null;
  interval: Interval;
  sub: Sub;
  onClose: () => void;
  confirm: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const tz = useSession().me.tenant.timeZone;
  const from = (sub.plans ?? []).find((p) => p.current);
  const fromInterval = (sub.billingInterval ?? "monthly") as Interval;
  const price = plan ? periodPrice(plan, interval) : null;
  const fromPrice = from ? periodPrice(from, fromInterval) : null;
  const upgrade =
    price !== null && fromPrice !== null
      ? perMonth(price, interval) > perMonth(fromPrice, fromInterval)
      : true;
  const sameplan = plan !== null && from?.planKey === plan.planKey;
  const limit = plan?.teamsLimit ?? 0;
  const tooSmall = plan !== null && limit !== -1 && (sub.teamsUsed ?? 0) > limit;
  return (
    <Dialog
      open={plan !== null}
      onClose={onClose}
      title={
        sameplan
          ? t(`ui:subscription.changeTitleInterval.${interval}`)
          : t("ui:subscription.changeTitle", { plan: plan?.displayName ?? "" })
      }
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button variant="primary" onClick={confirm}>
            {t("ui:subscription.changeConfirm")}
          </Button>
        </>
      }
    >
      <div className="gp-stack">
        <section className="gp-order" aria-label={t("ui:subscription.changeSummary")}>
          <div>
            <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
              {t("ui:subscription.changeFrom", {
                from: `${from?.displayName ?? "—"} · ${t(`ui:subscription.intervalName.${fromInterval}`)}`,
              })}
            </p>
            <p className="t-body-strong" style={{ margin: 0 }}>
              {plan?.displayName} · {t(`ui:subscription.intervalName.${interval}`)}
            </p>
          </div>
          <div className="gp-order-price">
            <span className="t-title-md">{price === null ? "—" : formatMoney(price, locale)}</span>
            <span className="t-body-sm gp-muted">
              {t(interval === "annual" ? "ui:subscription.perYear" : "ui:subscription.perMonth")} ·{" "}
              {t("ui:subscription.excl")}
            </span>
          </div>
        </section>
        <p className="t-body" style={{ margin: 0 }}>
          {upgrade
            ? t("ui:subscription.changeNow", { card: sub.paymentMethodLabel ?? "" })
            : t("ui:subscription.changeRenewal", {
                date: formatDate(sub.currentPeriodEnd, locale, tz),
                current: from?.displayName ?? "",
              })}
        </p>
        {tooSmall ? (
          <Banner tone="warn">{t("ui:subscription.changeTooSmall", { n: sub.teamsUsed ?? 0, limit })}</Banner>
        ) : null}
      </div>
    </Dialog>
  );
}
